// Active-tenant selection (ENG-2387, consuming the ENG-2409 primitive).
//
// The backend deliberately persists NO current-tenant. A switch is caller-scoped:
// the console holds the selection here and re-asserts it as `X-Haven-Active-Tenant`
// on every request, which the server re-authorizes each time (403 on a forged or
// stale value — see ActiveTenant.kt). So this module is a *selection*, never a
// grant: losing or forging it can only ever cost access, never widen it.
//
// Why client-side at all: a persisted server-side "current tenant" would leak into
// the extension, which polls the same /subscription-status. An MSP tech switching
// to a client tenant in the console would have their OWN Shield adopt that client's
// whitelist and protected sites. Keeping the selection here — and off every request
// the extension makes — is what structurally prevents that.

const ACTIVE_TENANT_KEY = "havenActiveTenantId";

/** Header the backend authorizes the console's selected tenant from. */
export const ACTIVE_TENANT_HEADER = "X-Haven-Active-Tenant";

/**
 * The tenant the console is currently acting in, or null when the caller is in
 * their own default tenant (the overwhelmingly common case — solo users, and any
 * admin who has never switched).
 */
export function getActiveTenantId(): string | null {
  try {
    const value = localStorage.getItem(ACTIVE_TENANT_KEY);
    return value && value.trim() ? value : null;
  } catch {
    // Private mode / storage disabled: no selection is the safe answer.
    return null;
  }
}

/**
 * Select a tenant. Passing null (or the caller's own tenant — the caller's job to
 * detect) removes the override entirely, so no header is sent and the request path
 * is byte-identical to pre-ENG-2409 behavior.
 */
export function setActiveTenantId(tenantId: string | null): void {
  try {
    if (tenantId) localStorage.setItem(ACTIVE_TENANT_KEY, tenantId);
    else localStorage.removeItem(ACTIVE_TENANT_KEY);
  } catch {
    // Storage failures are non-fatal: the switch simply doesn't stick.
  }
}

export function clearActiveTenantId(): void {
  setActiveTenantId(null);
}

/**
 * Run [handler] when ANOTHER tab changes the selection (ENG-2573).
 *
 * The selection lives in localStorage, which every tab shares, and `authenticatedFetch` re-reads it on
 * every call — but only the switching tab reloads. So a second tab kept rendering the previous tenant
 * while silently sending the new tenant's header: it showed your own org and wrote to a client's. An
 * invite typed there granted a stranger access to the wrong tenant, with no error and nothing visibly
 * wrong afterwards. The backend can't catch it either, because the header is legitimately authorized
 * for this caller.
 *
 * The `storage` event fires only in tabs OTHER than the writer, which is exactly the set that needs to
 * know. Callers reload, matching what switchTenant already does locally and for the reason it gives:
 * a dozen hooks hold tenant-scoped data, so re-fetching some of them would just render one tenant's
 * data under another's name.
 *
 * `event.key === null` means the whole store was cleared (a sign-out elsewhere), which also invalidates
 * the selection, so it counts.
 *
 * @returns an unsubscribe function.
 */
export function onActiveTenantChangedInAnotherTab(handler: () => void): () => void {
  const listener = (event: StorageEvent) => {
    if (event.key !== null && event.key !== ACTIVE_TENANT_KEY) return;
    if (event.key === ACTIVE_TENANT_KEY && event.newValue === event.oldValue) return;
    handler();
  };
  window.addEventListener("storage", listener);
  return () => window.removeEventListener("storage", listener);
}

/**
 * Will the backend accept a tenant named in the header from this sign-in at all? (ENG-3421)
 *
 * The header is authorized against the caller's email only once the email is proven (ActiveTenant.kt
 * via `emailForAuthz`, ENG-2464). An unverified password sign-in is refused whatever it names,
 * because Firebase lets anyone register any address that way (ENG-2344). The account app has no
 * verification flow, so that is every password user. For them a named tenant is a 403, while the
 * request without it still works, so a caller that would name one checks here first.
 *
 * This mirrors `FirebaseTokenValidator.emailForAuthz` on the same token the request will carry, and
 * has to change when it does. Takes `getIdTokenResult()`'s shape.
 */
export function signInMayNameTenant(token: { signInProvider: string | null; claims: Record<string, unknown> }): boolean {
  const email = token.claims.email;
  if (typeof email !== "string" || email.trim() === "") return false;
  if (!token.signInProvider) return false; // provider unknown: the backend fails closed
  return !(token.signInProvider === "password" && token.claims.email_verified !== true);
}

/**
 * True when the console is acting in a tenant OTHER than the caller's own.
 *
 * Read this before pushing anything tenant-scoped to the extension. The extension
 * polls the backend itself with no active-tenant header, so it always resolves the
 * operator's real tenant — it does not need the console's help, it needs the console
 * not to hand it another tenant's policy.
 */
export function isTenantOverridden(): boolean {
  return getActiveTenantId() !== null;
}
